import React, { useLayoutEffect, useRef } from "react";

/**
 * Inline processing state for user-initiated writes (Place Order, Join League,
 * Create League, Create Post, Save changes, Send message, …).
 *
 * Behaviour:
 *   1. User taps.
 *   2. The label swaps to a short gerund (no ellipsis).
 *   3. The button stops accepting input — no click, no keyboard, no double
 *      submit. That block is a code flag (`disabled`), not a visual style.
 *   4. When the request resolves the processing state is removed. A resolved
 *      request must never leave the button stuck.
 *
 * NO SPINNER. The ticket originally called for a small circular indicator to
 * the left of the label, but the product decision is no buffer circles anywhere
 * site-wide, so the LABEL SWAP is the entire visual signal. It still does both
 * jobs the indicator was there for: it confirms the tap registered, and
 * `disabled` prevents a duplicate submission.
 *
 * NO colour change while processing. Greying reads as "permanently
 * unavailable" rather than "momentarily busy". Busy/disabled is still exposed
 * correctly to assistive tech via aria-busy / aria-disabled.
 *
 * The processing state is never the confirmation. Success and failure are
 * communicated afterwards by the existing toast / result state.
 *
 * `unavailable` is the SEPARATE state for "required data or permissions are not
 * ready yet". That one keeps the screen's existing greyed treatment, which the
 * caller passes through `unavailableClassName`.
 *
 * Capitalisation is the caller's: pass the exact string already in the UI.
 */
const ProcessingButton = ({
  processing = false,
  unavailable = false,
  label,
  processingLabel,
  children,
  className = "",
  unavailableClassName = "",
  type = "button",
  onClick,
  style,
  ...rest
}) => {
  const ref = useRef(null);
  const idleWidthRef = useRef(null);

  // Lock the width the button had while idle so the swap to "Saving" cannot
  // reflow or clip it. `w-full` buttons are unaffected — their width is already
  // fixed by the container.
  useLayoutEffect(() => {
    if (!processing && ref.current) {
      idleWidthRef.current = ref.current.offsetWidth || null;
    }
  }, [processing, label]);

  const blocked = processing || unavailable;
  const content = label ?? children;

  return (
    <button
      ref={ref}
      type={type}
      // Real disabled attribute: kills click, Enter/Space, and form submit, so
      // a double submit is impossible.
      disabled={blocked}
      aria-busy={processing ? "true" : undefined}
      aria-disabled={blocked ? "true" : undefined}
      onClick={blocked ? undefined : onClick}
      className={`${className} ${
        unavailable ? unavailableClassName : ""
      } ${processing ? "cursor-default" : ""}`}
      style={
        processing && idleWidthRef.current
          ? { minWidth: idleWidthRef.current, ...style }
          : style
      }
      {...rest}
    >
      {processing ? (
        <span className="whitespace-nowrap">{processingLabel}</span>
      ) : (
        content
      )}
    </button>
  );
};

export default ProcessingButton;
